<script setup lang="ts">
// The environment's application logs (docs/proposal-application-logs.md): GET /environments/{name}/app-logs. The API
// pins every query to this environment and decides who may read; this offers text search, time range, level,
// application and category filters, paging older lines, and a live follow (polling with summary=false).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { must } from '../api/client';
import type { AppLogLine, AppLogs, LogCount } from '../api/types';
import { useContext } from '../context';
import ProblemAlert from './ProblemAlert.vue';
import Icon from './Icon.vue';
import { levelTone, lineKey, lineTime, logText, RANGES, type RangeKey } from '../logs/appLogs';

const props = defineProps<{ environment: string; raw?: boolean; source?: string }>();
const { api } = useContext();

const q = ref('');
const range = ref<RangeKey>('1h');
const level = ref<'all' | 'warn' | 'error'>('all');
const apps = ref<string[]>([]);
const categories = ref<string[]>([]);
const catFilter = ref('');
const live = ref(false);

const lines = ref<AppLogLine[]>([]);
const summary = ref<AppLogs>();
const more = ref(false);
const loading = ref(false);
const error = ref<unknown>();
const open = ref<Set<string>>(new Set());

const LIMIT = 200;
const LIVE_MS = 5000;
const KEEP = 2000;
let seq = 0;
let liveTimer: number | undefined;
let searchTimer: number | undefined;

function base() {
  return {
    app: apps.value.length ? apps.value : undefined,
    category: categories.value.length ? categories.value : undefined,
    level: level.value,
    q: q.value.trim() || undefined,
  };
}

async function get(query: Record<string, unknown>): Promise<AppLogs> {
  return must(await api.GET('/environments/{name}/app-logs', { params: { path: { name: props.environment }, query: query as never } }));
}

/** A new query: the summary, the first page. */
async function run() {
  const mine = ++seq;
  loading.value = true;
  try {
    const from = new Date(Date.now() - RANGES[range.value].ms).toISOString();
    const r = await get({ ...base(), from, limit: LIMIT });
    if (mine !== seq) return;
    summary.value = r;
    lines.value = r.lines;
    more.value = r.more;
    open.value = new Set();
    error.value = undefined;
  } catch (e) {
    if (mine === seq) error.value = e;
  } finally {
    if (mine === seq) loading.value = false;
  }
}

/** Older lines of the same range: to = the oldest line shown. */
async function older() {
  const s = summary.value;
  const last = lines.value[lines.value.length - 1];
  if (!s || !last?.time) return;
  const mine = seq;
  loading.value = true;
  try {
    const r = await get({ ...base(), from: s.from, to: last.time, limit: LIMIT, summary: false });
    if (mine !== seq) return;
    const have = new Set(lines.value.map(lineKey));
    lines.value = [...lines.value, ...r.lines.filter((l) => !have.has(lineKey(l)))];
    more.value = r.more;
  } catch (e) {
    error.value = e;
  } finally {
    loading.value = false;
  }
}

/** Live: what arrived since the newest line shown, without counts (not audited again). */
async function follow() {
  const newest = lines.value[0]?.time ?? summary.value?.to;
  if (!newest) return;
  const mine = seq;
  try {
    const r = await get({ ...base(), from: newest, limit: 500, summary: false });
    if (mine !== seq || !live.value) return;
    const have = new Set(lines.value.map(lineKey));
    const fresh = r.lines.filter((l) => !have.has(lineKey(l)));
    if (fresh.length) lines.value = [...fresh, ...lines.value].slice(0, KEEP);
    error.value = undefined;
  } catch (e) {
    error.value = e;
  }
}

function scheduleLive() {
  window.clearTimeout(liveTimer);
  if (live.value) liveTimer = window.setTimeout(async () => { await follow(); scheduleLive(); }, LIVE_MS);
}

watch([range, level, apps, categories], () => void run(), { deep: true });
watch(q, () => {
  window.clearTimeout(searchTimer);
  searchTimer = window.setTimeout(() => void run(), 400);
});
watch(live, (on) => { if (on) void follow(); scheduleLive(); });
onMounted(run);
onBeforeUnmount(() => { window.clearTimeout(liveTimer); window.clearTimeout(searchTimer); seq++; });

const flip = (list: string[], value: string) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
const toggleApp = (value: string) => { apps.value = flip(apps.value, value); };
const toggleCat = (value: string) => { categories.value = flip(categories.value, value); };
function toggleOpen(l: AppLogLine) {
  const k = lineKey(l);
  const next = new Set(open.value);
  if (next.has(k)) next.delete(k); else next.add(k);
  open.value = next;
}

/** The counts, with every chosen value listed even when it has no line in the range. */
function facet(counts: LogCount[] | null | undefined, chosen: string[]): LogCount[] {
  const list = [...(counts ?? [])];
  for (const c of chosen) if (!list.some((x) => x.value === c)) list.push({ value: c, hits: 0 });
  return list;
}
const appFacet = computed(() => facet(summary.value?.apps, apps.value));
const catFacet = computed(() => {
  const f = catFilter.value.trim().toLowerCase();
  return facet(summary.value?.categories, categories.value).filter((c) => !f || c.value.toLowerCase().includes(f) || categories.value.includes(c.value));
});

const bars = computed(() => {
  const b = summary.value?.histogram?.buckets ?? [];
  const max = Math.max(1, ...b.map((x) => x.hits));
  return b.map((x) => ({ key: x.value, title: `${lineTime(x.value)} · ${x.hits.toLocaleString('en')} lines`, height: `${Math.max(x.hits ? 2 : 0, Math.round((x.hits / max) * 64))}px` }));
});
const total = computed(() => (summary.value?.total ?? lines.value.length).toLocaleString('en'));

function download() {
  const blob = new Blob([lines.value.map(logText).join('\n') + '\n'], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${props.environment}-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
  a.click();
  URL.revokeObjectURL(a.href);
}
</script>

<template>
  <div class="applogs">
    <p v-if="raw" class="alert banner" role="note">
      <Icon name="lock" :size="18" />
      <span class="grow"><strong>Raw production data.</strong> These logs may hold customer data: you see them because you are on the access list
        and entitled to <code>{{ source }}</code>. Every query is audited.</span>
    </p>

    <section class="card applogs-bar" aria-label="Query">
      <label class="applogs-search">
        <span class="sr-only">Search the messages</span>
        <Icon name="search" :size="16" />
        <input v-model="q" type="search" placeholder="Search the messages, e.g. kafka_export or an order number" maxlength="200" data-testid="logs-q" />
      </label>
      <div class="seg" role="radiogroup" aria-label="Time range">
        <label v-for="(r, k) in RANGES" :key="k"><input v-model="range" type="radio" name="logs-range" :value="k" :data-testid="`logs-range-${k}`" />{{ r.label }}</label>
      </div>
      <div class="seg" role="radiogroup" aria-label="Level">
        <label><input v-model="level" type="radio" name="logs-level" value="all" />All</label>
        <label><input v-model="level" type="radio" name="logs-level" value="warn" data-testid="logs-level-warn" />Warn+</label>
        <label><input v-model="level" type="radio" name="logs-level" value="error" />Errors</label>
      </div>
      <button type="button" class="button sm" :class="live ? 'tone-ok' : ''" :aria-pressed="live" data-testid="logs-live" @click="live = !live">
        <span class="live-dot" :class="{ on: live }" aria-hidden="true"></span>{{ live ? 'Live · following' : 'Live' }}
      </button>
      <button type="button" class="button sm" :disabled="!lines.length" aria-label="Download the shown lines" title="Download the shown lines" @click="download">Download</button>
    </section>

    <ProblemAlert :error="error" context="Logs" />

    <div class="cols">
      <aside class="card applogs-facets" aria-label="Filters">
        <fieldset>
          <legend>Application</legend>
          <label v-for="a in appFacet" :key="a.value" class="facet"><input type="checkbox" :checked="apps.includes(a.value)" :data-testid="`logs-app-${a.value}`" @change="toggleApp(a.value)" /> {{ a.value }} <span class="n">{{ a.hits.toLocaleString('en') }}</span></label>
          <p v-if="!appFacet.length" class="hint">No application logged in this range.</p>
        </fieldset>
        <fieldset>
          <legend>Category <span class="hint-inline">· shop only</span></legend>
          <label class="sr-only" for="logs-cat-filter">Find a category</label>
          <input id="logs-cat-filter" v-model="catFilter" type="search" placeholder="Find a category" class="applogs-catq" />
          <label v-for="c in catFacet" :key="c.value" class="facet"><input type="checkbox" :checked="categories.includes(c.value)" :data-testid="`logs-cat-${c.value}`" @change="toggleCat(c.value)" /> {{ c.value }} <span class="n">{{ c.hits.toLocaleString('en') }}</span></label>
          <button v-if="categories.length" type="button" class="link" @click="categories = []">Clear categories</button>
        </fieldset>
        <p class="hint">None ticked shows all. Counts are for the chosen range; logs are kept 30 days.</p>
      </aside>

      <section class="card applogs-main" aria-labelledby="logs-h">
        <div class="applogs-head">
          <h2 id="logs-h">{{ total }} lines</h2>
          <span class="muted">{{ live ? 'live' : RANGES[range].text }}</span>
          <span v-if="loading" class="muted" aria-live="polite">loading…</span>
          <span class="muted end">newest first<template v-if="summary?.histogram"> · bars per {{ summary.histogram.stepSeconds }} s</template></span>
        </div>
        <div class="applogs-hist" aria-hidden="true">
          <span v-for="b in bars" :key="b.key" :title="b.title" :style="{ height: b.height }"></span>
        </div>
        <div class="applogs-scroll">
          <div role="log" aria-label="Log lines" class="applogs-table" data-testid="logs-lines">
            <div class="row head"><span></span><span>Time (UTC)</span><span>Level</span><span>Application</span><span>Category</span><span>Message</span></div>
            <template v-for="l in lines" :key="lineKey(l)">
              <div class="row">
                <button type="button" class="chev" :aria-expanded="open.has(lineKey(l))" aria-label="Show all fields" @click="toggleOpen(l)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true" :style="open.has(lineKey(l)) ? 'transform: rotate(90deg)' : ''"><path d="M9 6l6 6-6 6" /></svg>
                </button>
                <span class="t">{{ lineTime(l.time) }}</span>
                <span><span v-if="l.level" class="lv" :class="levelTone(l.level)">{{ l.level }}</span></span>
                <span><span v-if="l.app" class="tag">{{ l.app }}</span></span>
                <span><span v-if="l.category" class="tag">{{ l.category }}</span><span v-else class="muted">—</span></span>
                <span class="msg">{{ l.message }}</span>
              </div>
              <pre v-if="open.has(lineKey(l))" class="fields">{{ JSON.stringify(l.fields, null, 2) }}</pre>
            </template>
            <p v-if="!lines.length && !loading" class="empty">No line matches. Widen the time range or clear a filter.</p>
          </div>
        </div>
        <div class="applogs-foot">
          <span class="muted">Showing {{ lines.length.toLocaleString('en') }} of {{ total }}</span>
          <button v-if="more" type="button" class="button sm end" :disabled="loading" data-testid="logs-older" @click="older">Load older lines</button>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.applogs { display: flex; flex-direction: column; gap: 1.25rem; }
.applogs-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem 0.75rem; padding: 0.85rem 1rem; }
.applogs-search { position: relative; flex: 1 1 320px; min-width: 0; display: flex; align-items: center; }
.applogs-search :deep(svg) { position: absolute; left: 11px; color: var(--muted); pointer-events: none; }
.applogs-search input { width: 100%; padding-left: 34px; }
.live-dot { width: 8px; height: 8px; border-radius: 50%; background: #9aa3b0; display: inline-block; margin-right: 6px; }
.live-dot.on { background: #1f9d5b; }
.applogs-facets { flex: 1 1 240px; min-width: 0; display: flex; flex-direction: column; gap: 1.1rem; }
.applogs-facets fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
.applogs-facets legend { font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--muted); margin-bottom: 6px; }
.hint-inline { text-transform: none; letter-spacing: 0; font-weight: 400; }
.applogs-catq { min-height: 30px; font-size: 13px; margin-bottom: 6px; width: 100%; box-sizing: border-box; }
.facet { display: flex; align-items: center; gap: 8px; min-height: 30px; font-size: 13.5px; font-weight: 400; cursor: pointer; margin: 0; }
.facet input { width: 16px; height: 16px; min-height: 0; accent-color: var(--accent); flex: none; }
.facet .n { margin-left: auto; color: var(--muted); font-size: 12.5px; font-variant-numeric: tabular-nums; }
.applogs-main { flex: 999 1 640px; min-width: 0; padding: 0; overflow: hidden; }
.applogs-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 14px; padding: 14px 16px 6px; font-size: 13px; }
.applogs-head h2 { margin: 0; font-size: 17px; }
.applogs-head .end, .applogs-foot .end { margin-left: auto; }
.applogs-hist { display: flex; align-items: flex-end; gap: 2px; height: 72px; padding: 0 16px 8px; border-bottom: 1px solid var(--line); }
.applogs-hist span { flex: 1 1 0; min-width: 2px; border-radius: 2px 2px 0 0; background: #b8c7f0; }
.applogs-scroll { overflow-x: auto; max-height: 70vh; overflow-y: auto; }
.applogs-table { min-width: 900px; }
.row { display: grid; grid-template-columns: 18px 178px 72px 112px 128px minmax(0, 1fr); gap: 10px; align-items: baseline; padding: 5px 14px; border-bottom: 1px solid var(--line-soft, #eef0f3); font-family: var(--mono); font-size: 12.5px; line-height: 1.55; }
.row.head { position: sticky; top: 0; background: var(--panel, #fafbfc); font-family: inherit; font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; z-index: 1; }
.row:not(.head):hover { background: var(--bg); }
.row .t { color: #454d5a; }
.row .msg { white-space: pre-wrap; overflow-wrap: anywhere; }
.chev { all: unset; cursor: pointer; color: var(--muted); display: inline-flex; width: 18px; height: 18px; align-items: center; justify-content: center; border-radius: 4px; }
.chev:focus-visible { outline: 2px solid var(--focus); }
.lv { display: inline-flex; align-items: center; min-height: 20px; padding: 0 6px; border-radius: 4px; font-size: 11.5px; font-weight: 600; }
.tag { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 0 6px; border-radius: 4px; background: var(--idle-bg); font-family: inherit; font-size: 11.5px; line-height: 20px; vertical-align: bottom; }
.fields { margin: 0; padding: 10px 16px 12px 44px; background: var(--bg); border-bottom: 1px solid var(--line); font-family: var(--mono); font-size: 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
.empty { padding: 32px 16px; text-align: center; color: var(--muted); margin: 0; }
.applogs-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 12px 16px; border-top: 1px solid var(--line); font-size: 13px; }
</style>
